import React from "react";
import {Text, IconButton} from "@nike/podium";
import {Check, ExternalLink} from "@nike/nike-design-system-icons";
import {ImbalanceItem} from "../../common/data/types";

type ImbalanceTableProps = {
    pageItems: ImbalanceItem[];
    tableAreaRef?: React.RefObject<HTMLDivElement>;
};

export function ImbalanceTable({pageItems, tableAreaRef}: ImbalanceTableProps) {
    return (
        <div className="im-table-area" ref={tableAreaRef as any}>
            <table className="im-data-table">
                <thead>
                    <tr>
                        <th colSpan={5} style={{borderBottom: 'none', padding: '7px 12px', borderRight: '1px solid var(--im-border-subtle)'}} />
                        <th
                            colSpan={5}
                            style={{
                                textAlign: 'center', fontSize: 12, fontWeight: 700,
                                textTransform: 'uppercase', letterSpacing: 0.5,
                                borderBottom: 'none', padding: '7px 12px',
                                borderLeft: '1px solid var(--im-border-subtle)',
                                borderRight: '1px solid var(--im-border-subtle)',
                            }}
                        >
                            Latest balancing results
                        </th>
                        <th colSpan={4} style={{borderBottom: 'none', padding: '7px 12px', borderLeft: '1px solid var(--im-border-subtle)'}} />
                    </tr>
                    <tr>
                        <th style={{width: 32, padding: '7px 8px 7px 12px'}} />
                        <th>Material</th>
                        <th>Size</th>
                        <th>Quality</th>
                        <th style={{borderRight: '1px solid var(--im-border-subtle)'}}>COO</th>
                        <th style={{borderLeft: '1px solid var(--im-border-subtle)'}}>CI Bonded Qty</th>
                        <th>CI Non-bonded Qty</th>
                        <th>CI Total Qty</th>
                        <th>WMS Qty</th>
                        <th style={{borderRight: '1px solid var(--im-border-subtle)'}}>Imbalance</th>
                        <th style={{borderLeft: '1px solid var(--im-border-subtle)'}}>Persistent imbalance</th>
                        <th>Direction</th>
                        <th>Clean status</th>
                        <th>Flow information</th>
                    </tr>
                </thead>
                <tbody>
                    {pageItems.length === 0 ? (
                        <tr>
                            <td colSpan={14} style={{textAlign: 'center', padding: 32}}>
                                <Text font="body-3" color="secondary">No lines match.</Text>
                            </td>
                        </tr>
                    ) : (
                        pageItems.map((item, idx) => (
                            <ImbalanceRow
                                key={`${item.material}-${item.size}-${idx}`}
                                item={item}
                            />
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

type ImbalanceRowProps = {
    item: ImbalanceItem;
};

function ImbalanceRow({item}: ImbalanceRowProps) {
    const {material, size, quality, coo, wmsQty, ciBondedQty, ciNonBondedQty, ciTotalQty, imbalance, persistentImbalance} = item;
    const isZero = wmsQty === 0;
    const isOver = wmsQty > ciTotalQty && wmsQty > 0;

    const renderImbalance = () => {
        if (isZero) return <span className="im-iv im-iv-zero">{imbalance}</span>;
        if (imbalance < 0) return <span className="im-iv im-iv-neg">{imbalance}</span>;
        return <span className="im-iv im-iv-pos">+{imbalance}</span>;
    };

    const renderPersistentImbalance = () => {
        if (persistentImbalance === 0) return <Text font="body-3" as="span" color="secondary">—</Text>;
        return (
            <span style={{fontWeight: 700, fontVariantNumeric: 'tabular-nums'}}>
                {persistentImbalance > 0 ? '+' : ''}{persistentImbalance}
            </span>
        );
    };

    const renderDirection = () => {
        if (isZero) return <span className="im-dir-txt">WMS = 0</span>;
        if (isOver) return <span className="im-dir-txt">WMS &gt; CI</span>;
        return <span className="im-dir-txt">WMS &lt; CI</span>;
    };

    const renderCleanStatus = () => {
        if (item.cleanStatus && item.cleanStatus !== 'Open') {
            return <span className="im-cs-processed"><Check size="s" />Submitted</span>;
        }
        return <span className="im-cs-open">Open</span>;
    };

    const renderFlowInfo = () => {
        if (item.flowInformation) {
            const flowUrl = `${window.location.origin}/flow-overview?flowId=${encodeURIComponent(item.flowInformation)}`;
            return (
                <IconButton
                    aria-label="View flow overview"
                    size="xs"
                    variant="outlined"
                    onClick={() => window.open(flowUrl, '_blank', 'noopener,noreferrer')}
                >
                    <ExternalLink size="s" />
                </IconButton>
            );
        }
        return <Text font="body-3" as="span" color="secondary">—</Text>;
    };

    return (
        <tr>
            <td className="im-td-detail">
                <IconButton
                    aria-label="View inventory overview details"
                    size="xs"
                    variant="outlined"
                    onClick={() => {
                        const url = `${window.location.origin}/inventory-overview/details/${encodeURIComponent(material)}/${encodeURIComponent(size)}/${encodeURIComponent(coo)}/${encodeURIComponent(quality)}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                >
                    <ExternalLink size="s" />
                </IconButton>
            </td>
            <td><strong>{material}</strong></td>
            <td>{size}</td>
            <td>{quality}</td>
            <td>{coo}</td>
            <td>{ciBondedQty > 0 ? ciBondedQty : <span style={{color: 'var(--pds-color-text-secondary)'}}>0</span>}</td>
            <td>{ciNonBondedQty > 0 ? ciNonBondedQty : <span style={{color: 'var(--pds-color-text-secondary)'}}>0</span>}</td>
            <td><strong>{ciTotalQty}</strong></td>
            <td>{wmsQty === 0 ? <span style={{fontWeight: 600}}>0</span> : wmsQty}</td>
            <td>{renderImbalance()}</td>
            <td>{renderPersistentImbalance()}</td>
            <td>{renderDirection()}</td>
            <td style={{whiteSpace: 'nowrap'}}>{renderCleanStatus()}</td>
            <td>{renderFlowInfo()}</td>
        </tr>
    );
}
